/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useHotDeskingForm.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing hot desking form state, validation, and submission.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { createHotDeskingDevice, updateHotDeskingDevice } from '../api/hotDeskingApi';
import type { HotDeskFormState, ValidationErrors } from '../types';

interface UseHotDeskingFormOptions {
  formData: HotDeskFormState;
  setFormData: React.Dispatch<React.SetStateAction<HotDeskFormState>>;
  originalFormData: HotDeskFormState;
  isEditMode: boolean;
  extensionId?: string;
}

export function useHotDeskingForm({
  formData,
  setFormData,
  originalFormData,
  isEditMode,
  extensionId,
}: UseHotDeskingFormOptions) {
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  }, [formData, originalFormData]);

  // Validation
  const validateForm = useCallback(() => {
    const newErrors: ValidationErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = t('hotDesking.validation.usernameRequired');
    }

    if (!formData.password.trim()) {
      newErrors.password = t('hotDesking.validation.passwordRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);

  // Handle save - Always-Active Save Standard: always attempt to save
  const handleSave = useCallback(async () => {
    if (!validateForm()) {
      showError(t('hotDesking.validation.formError'));
      return undefined;
    }

    setIsSaving(true);
    try {
      const payload = {
        username: formData.username,
        password: formData.password,
        description: formData.description,
        sipProfileId: formData.sipProfileId,
        dispatchLocationId: formData.dispatchLocationId ?? null,
        maxRegistrations: formData.maxRegistrations,
        outboundCodecs: formData.outboundCodecs,
        inboundCodecs: formData.inboundCodecs,
        dtmfMode: formData.dtmfMode,
        language: formData.language,
        enabled: formData.enabled,
      };

      let savedDevice;
      if (isEditMode && extensionId) {
        savedDevice = await updateHotDeskingDevice(extensionId, payload);
        showSuccess(t('hotDesking.messages.updateSuccess'));
      } else {
        savedDevice = await createHotDeskingDevice(payload);
        showSuccess(t('hotDesking.messages.createSuccess'));
        // Note: Navigation after create is now handled by the dropdown actions
        // in HotDeskingFormView, so we don't navigate here automatically
      }

      return savedDevice;
    } catch (error) {
      console.error('Error saving hot desking device:', error);
      const msg = error instanceof Error ? error.message : t('hotDesking.messages.saveError');
      showError(msg);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [
    formData,
    validateForm,
    isEditMode,
    extensionId,
    showSuccess,
    showError,
    t,
  ]);

  // Handle cancel - revert changes and stay on form
  const handleCancel = useCallback(() => {
    if (hasChanges) {
      setShowCancelModal(true);
    }
    // If no changes, do nothing (stay on form)
  }, [hasChanges]);

  const confirmCancel = useCallback(() => {
    setShowCancelModal(false);
    setFormData({ ...originalFormData });
    setErrors({});
  }, [originalFormData, setFormData, setErrors]);

  return {
    errors,
    setErrors,
    isSaving,
    hasChanges,
    validateForm,
    handleSave,
    handleCancel,
    showCancelModal,
    setShowCancelModal,
    confirmCancel
  };
}


